import { debounce } from 'lodash';
import {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

interface Props extends ComponentProps<typeof Scrollbars> {
  observeResize?: boolean;
  resizeDebounceMs?: number;
}

interface ScrollbarsWithView extends Scrollbars {
  view?: HTMLDivElement;
  update: () => void;
}

const ResizeScrollbars = forwardRef<Scrollbars, Props>(
  (
    { observeResize = true, resizeDebounceMs = 50, ...props },
    forwardedRef: ForwardedRef<Scrollbars>
  ) => {
    const scrollbarsRef = useRef<ScrollbarsWithView>(null);

    useImperativeHandle(forwardedRef, () => scrollbarsRef.current as Scrollbars);

    const debouncedUpdate = useMemo(
      () =>
        debounce(() => {
          scrollbarsRef.current?.update();
        }, resizeDebounceMs),
      [resizeDebounceMs]
    );

    useEffect(() => {
      if (!observeResize) {
        return;
      }
      if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
        return;
      }

      const view = scrollbarsRef.current?.view;
      if (!view) {
        return;
      }

      const handleUpdate = () => {
        debouncedUpdate();
      };

      const resizeObserver = new ResizeObserver(handleUpdate);
      const mutationObserver = new MutationObserver(handleUpdate);

      resizeObserver.observe(view);
      mutationObserver.observe(view, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, [observeResize, debouncedUpdate]);

    useEffect(() => {
      return () => {
        debouncedUpdate.cancel();
      };
    }, [debouncedUpdate]);

    return <Scrollbars ref={scrollbarsRef} {...props} />;
  }
);

ResizeScrollbars.displayName = 'ResizeScrollbars';

export default ResizeScrollbars;
